import React, { useState } from 'react';
import SEO from '../components/SEO';

const TFN = '+1 (855) 895-8503';
const TEL = 'tel:+18558958503';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Flight Change Help | Flyairlinebooking',
  url: 'https://flyairlinebooking.com/flightchanges',
  description:
    'Need to change a flight? Call Flyairlinebooking 24/7 for help changing dates, times, or routes on an existing booking, based on the operating airline\'s policy.',
  publisher: {
    '@type': 'Organization',
    name: 'Flyairlinebooking',
    url: 'https://flyairlinebooking.com',
    telephone: TFN,
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can Flyairlinebooking change my flight directly with the airline?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We are an independent travel agency, not an airline. We contact the operating airline on your behalf, following its published change policy, to request and process your flight change.',
      },
    },
    {
      '@type': 'Question',
      name: 'Will I have to pay an airline change fee?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Possibly. Change fees and fare differences are set entirely by the operating airline based on your fare type. We disclose any applicable airline fee plus our own service fee before you confirm.',
      },
    },
    {
      '@type': 'Question',
      name: 'How quickly can a flight change be made?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Most changes are processed the same day, subject to seat availability on the new flight and the airline's own rules. Call ${TFN} and an agent will confirm options in real time.`,
      },
    },
    {
      '@type': 'Question',
      name: 'What if my flight was changed or cancelled by the airline?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If the airline initiates a schedule change or cancellation, its own policy determines your rebooking or refund options. Call us and we\'ll help you understand and act on those options.',
      },
    },
  ],
};

const STEPS = [
  { icon: '📞', title: 'Call Us', desc: `Reach a live agent anytime at ${TFN} with your booking reference ready.` },
  { icon: '🔎', title: 'We Check the Airline\'s Rules', desc: 'We look up the operating airline\'s current change policy for your specific fare type.' },
  { icon: '💬', title: 'We Explain Your Options', desc: 'New flight availability, any airline change fee, and fare difference — all explained before you decide.' },
  { icon: '✅', title: 'Change Confirmed', desc: 'Once approved, we process the change and send an updated confirmation by email.' },
];

const CHANGE_TYPES = [
  { icon: '📅', title: 'Date & Time Changes', desc: 'Move your flight to an earlier or later date/time, subject to seat availability and the airline\'s fare rules for that change.' },
  { icon: '🛫', title: 'Route / Airport Changes', desc: 'Switch origin or destination airport where the airline\'s fare rules and re-issuance policy allow it.' },
  { icon: '👤', title: 'Name Corrections', desc: 'Minor spelling corrections are handled per airline policy; some airlines restrict full name changes entirely.' },
  { icon: '⚠️', title: 'Airline-Initiated Changes', desc: 'If the airline changes your schedule, its policy governs your rebooking, refund, or compensation options — we help you navigate it.' },
];

export default function FlightChangesPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const FAQS = faqJsonLd.mainEntity.map(q => ({ q: q.name, a: q.acceptedAnswer.text }));

  return (
    <div className="bg-white">
      {/* Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-100 py-2.5 px-4 text-center text-xs text-amber-800">
        <strong>Disclaimer:</strong> Flyairlinebooking is an independent travel agency and does not represent, own, or act on behalf of any airline. All change policies, fees, and rules are set by the operating airline.
      </div>

      <SEO
        title="Flight Change Help — Call to Reschedule"
        canonical="/flightchanges"
        description="Need to change your flight? Call Flyairlinebooking 24/7. Our agents check the operating airline's change policy and help you reschedule your booking."
        keywords="flight changes, change flight date, reschedule flight, flight change fee, airline change policy"
        jsonLd={[jsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-indigo-950 via-indigo-900 to-indigo-800 text-white overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-10 sm:pt-16 sm:pb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-4">
            🔄 Independent Flight Change Assistance
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
            Need to Change a Flight?<br className="hidden sm:block" />
            <span className="text-indigo-300">Call a Live Agent, 24/7</span>
          </h1>
          <p className="text-indigo-100 text-base sm:text-lg mt-3 max-w-xl mx-auto">
            We help you reschedule your existing booking according to the operating airline's own change policy — we don't represent any airline.
          </p>

          <div className="mt-7">
            <a
              href={TEL}
              className="inline-flex items-center justify-center gap-2 bg-white text-indigo-900 font-bold px-8 py-4 rounded-xl hover:bg-indigo-50 transition-colors text-lg shadow-lg"
            >
              📞 Call {TFN} to Change Your Flight
            </a>
            <p className="text-indigo-200 text-xs mt-3">Available 24 hours a day, 7 days a week</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">How Flight Change Help Works</h2>
          <p className="text-gray-500">Four simple steps to get rebooked.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 relative">
              <span className="absolute top-4 right-4 text-xs font-bold text-indigo-300">{i + 1}</span>
              <div className="text-3xl mb-3">{s.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1.5">{s.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Types of changes / airline policy info */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Types of Flight Changes We Assist With</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Change eligibility and fees depend entirely on the airline operating your flight and the fare rules attached to your ticket.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {CHANGE_TYPES.map(t => (
              <div key={t.title} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex gap-4">
                <div className="text-3xl shrink-0">{t.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{t.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6 max-w-2xl mx-auto">
            We are not the airline and do not set airline change fees or fare rules. Our agents look up and explain the current policy for your specific airline and fare before any change is confirmed.
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-indigo-900 to-indigo-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Need to Reschedule Your Flight?</h2>
          <p className="text-indigo-200 mb-7 text-base">
            Call now with your booking reference and a live agent will check availability and the airline's change policy for you.
          </p>
          <a
            href={TEL}
            className="inline-flex items-center justify-center gap-2 bg-white text-indigo-900 font-bold px-8 py-3.5 rounded-xl hover:bg-indigo-50 transition-colors"
          >
            📞 Call {TFN}
          </a>
          <p className="text-indigo-300 text-xs mt-5">Independent travel agency · Not affiliated with any airline</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-800 text-sm leading-snug">{faq.q}</span>
                <span className={`text-indigo-500 font-bold text-lg shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 text-sm">
          Need to change an existing flight?{' '}
          <a href={TEL} className="text-indigo-600 font-semibold hover:underline">{TFN}</a>
          {' '}— available 24/7. Flyairlinebooking is an independent travel agency and is not affiliated with any airline.
        </p>
      </section>
    </div>
  );
}
